import { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { useInView, useIsPresent } from 'framer-motion';

import { type Masked, unmask } from '~/contentful/graphql';
import { Event } from '~/ui/components/analytics';
import { FullBleedCTAScope, FullBleedCTATrigger } from '~/ui/components/fullBleedCta';
import { Link } from '~/ui/components/link';
import { HeroAsset } from '~/ui/modules/heroAsset';
import { Breakpoint } from '~/ui/styles/grid';
import truncateStyles from '~/ui/styles/truncate.module.scss';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { getInternalLinkFromFragment } from '~/ui/systems/internalLink';

import { ANIMATION_DURATION } from '../constants';
import { type GrantStoryAccordionCard_GrantActivityArticleFragment } from '../query';
import styles from './card.module.scss';

export interface AccordionCardProps {
  data: Masked<typeof GrantStoryAccordionCard_GrantActivityArticleFragment>;
  className?: string;
}

export function AccordionCard({ data, className }: AccordionCardProps): React.ReactElement {
  const [enabled, setEnabled] = useState<boolean>(false);
  const previewVideoRef = useRef<HTMLVideoElement>(null!);
  const isPreviewVideoInView = useInView(previewVideoRef);
  const isActiveCard = useIsPresent();

  const article = unmask<typeof GrantStoryAccordionCard_GrantActivityArticleFragment>(data);
  const resolvedAsset = article.hero ?? article.leadImage;

  useEffect(() => {
    if (isActiveCard) {
      setTimeout(() => setEnabled(true), ANIMATION_DURATION * 500);
    } else {
      setEnabled(false);
    }
  }, [isActiveCard]);

  useEffect(() => {
    if (isPreviewVideoInView) {
      previewVideoRef.current?.play();
    } else {
      previewVideoRef.current?.pause();
    }
  }, [isPreviewVideoInView]);

  return (
    <FullBleedCTAScope>
      <article
        className={cx(styles.card, className)}
        style={{ pointerEvents: enabled ? 'auto' : 'none' }}
      >
        <div className={styles.cardInner}>
          {resolvedAsset ? (
            <HeroAsset
              data={resolvedAsset}
              imageProps={{
                aspectRatio: '32/45',
                colSpan: { [Breakpoint.MOBILE]: 5 },
              }}
            />
          ) : null}
        </div>
        <div className={styles.cardContent}>
          <div>
            <div className={cx(typographyStyles.paragraph, styles.eyebrow)}>
              {article.grant?.organization?.name}
            </div>
            <h3 className={cx(typographyStyles.modulesTitle, truncateStyles.truncate3, styles.title)}>
              {article.title}
            </h3>
          </div>
          <FullBleedCTATrigger>
            <Link
              href={getInternalLinkFromFragment(article)}
              aria-label="Read the grant story"
              event={{
                name: Event.GRANT_ACCORDION_CLICK,
                data: {
                  accordionTitle: article.title,
                  accordionCTA: 'Read the grant story',
                  accordionLink: getInternalLinkFromFragment(article),
                },
              }}
              aria-disabled={!enabled}
              style={{
                pointerEvents: enabled ? 'auto' : 'none',
              }}
              withIcon
            >
              Read the grant story
            </Link>
          </FullBleedCTATrigger>
        </div>
      </article>
    </FullBleedCTAScope>
  );
}
